/* ==========================================================================
   Pop-up landing page — every upcoming pop-up date in a region, bookable in
   place. Drives sections/popup-lp-dates.liquid (config in [data-pld-config]).

   Data: BookThatApp's frames feed through the Shopify App Proxy — the same
   read-only, same-origin endpoint the booking widgets use. It takes several
   products at once (external_id=ID,ID,…; every timeslot carries its product's
   external_id) and refuses a window over 90 days, so the horizon is read as
   parallel 89-day windows.

   "Book this date" boots that city's lazy pop-up widget
   (window.PopupBooking.boot, see snippets/mas-booking-widget-popup.liquid),
   waits for its first render, picks the day and opens its pop-up dialog. The
   booking itself — sessions, projects, seats held, cart, checkout — is the
   widget's, unchanged. Nothing here reserves or adds to cart.

   Dates and times are in each studio's own time zone, never the visitor's.
   ========================================================================== */
(function () {
  'use strict';

  var DAY_MS = 86400000;
  var WINDOW_DAYS = 89;
  var TIMEOUT_MS = 30000;
  var TZ_ALIASES = {
    'US/Eastern': 'America/New_York', 'US/Central': 'America/Chicago',
    'US/Mountain': 'America/Denver', 'US/Arizona': 'America/Phoenix',
    'US/Pacific': 'America/Los_Angeles'
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function fill(str, key, val) { return String(str || '').split('[' + key + ']').join(val); }
  function tzOf(t) { return TZ_ALIASES[t] || t || 'America/Chicago'; }
  function fmt(iso, tz, opts) {
    var o = { timeZone: tz }; for (var k in opts) o[k] = opts[k];
    return new Intl.DateTimeFormat('en-US', o).format(new Date(iso));
  }
  function ymd(iso, tz) {
    var p = {};
    new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(iso)).forEach(function (x) { p[x.type] = x.value; });
    return p.year + '-' + p.month + '-' + p.day;
  }
  function daysBetween(a, b) {
    var u = function (k) { return Date.UTC(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10)); };
    return Math.round((u(b) - u(a)) / DAY_MS);
  }
  // Same rule as slotSeats() in assets/mas-booking-widget-popup.js, so the card
  // and the widget never disagree about how many seats are left.
  function slotSeats(slot) {
    var pool = (typeof slot.resource_availability === 'number') ? slot.resource_availability : null;
    var anyOpen = false, maxAvail = 0;
    (slot.availability || []).forEach(function (a) {
      var n = (a.status === 1) ? (a.available || 0) : 0;
      if (pool !== null) n = Math.min(n, pool);
      if (n > 0) anyOpen = true;
      if (n > maxAvail) maxAvail = n;
    });
    if (pool === null) pool = maxAvail;
    return anyOpen && pool > 0 ? pool : 0;
  }
  function track(evt, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: evt }; for (var k in data) payload[k] = data[k];
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the page */ }
    try {
      window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      window.clarity('event', 'mas_' + evt);
    } catch (e) { /* analytics must never break the page */ }
  }

  function frames(shop, ids, startISO, finishISO) {
    var url = '/apps/bookthatapp/api/v1/frames?external_id=' + ids.join(',') +
      '&start=' + encodeURIComponent(startISO) + '&finish=' + encodeURIComponent(finishISO) +
      '&shop=' + encodeURIComponent(shop);
    var opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;
    if (ctrl) { opts.signal = ctrl.signal; timer = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS); }
    return fetch(url, opts).then(function (r) {
      if (timer) clearTimeout(timer);
      if (!r.ok) throw new Error('frames HTTP ' + r.status);
      return r.json();
    });
  }

  function Landing(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector('[data-pld-config]').textContent);
    this.text = this.cfg.text || {};
    this.list = root.querySelector('[data-pld-list]');
    this.chips = root.querySelector('[data-pld-chips]');
    this.soon = root.querySelector('[data-pld-soon]');
    this.fallback = root.querySelector('[data-pld-fallback]');
    this.cities = {};
    var self = this;
    (this.cfg.cities || []).forEach(function (c) { c.tz = tzOf(c.tz); self.cities[String(c.id)] = c; });
    this.days = [];          // [{ city, date, slots, seats }]
    this.filter = 'all';
    this.load();
  }

  Landing.prototype.load = function () {
    var self = this, ids = Object.keys(this.cities);
    if (!ids.length) return this.fail();
    var days = Math.max(1, this.cfg.daysAhead || 178);
    var n = Math.ceil(days / WINDOW_DAYS), span = days / n, now = Date.now(), jobs = [];
    for (var i = 0; i < n; i++) {
      jobs.push(frames(this.cfg.shop, ids,
        new Date(now + Math.round(i * span * DAY_MS)).toISOString(),
        new Date(now + Math.round((i + 1) * span * DAY_MS)).toISOString()));
    }
    Promise.all(jobs).then(function (results) {
      var byKey = {}, seen = {}, nowMs = Date.now();
      results.forEach(function (data) {
        ((data && data.frames) || []).forEach(function (f) {
          (f.timeslots || []).forEach(function (s) {
            var city = self.cities[String(s.external_id)];
            if (!city || seen[s.id] || new Date(s.start).getTime() <= nowMs) return;
            seen[s.id] = 1;
            var key = city.id + '|' + ymd(s.start, city.tz);
            (byKey[key] = byKey[key] || { city: city, date: ymd(s.start, city.tz), slots: [] }).slots.push(s);
          });
        });
      });
      self.days = Object.keys(byKey).map(function (k) {
        var d = byKey[k];
        d.slots.sort(function (a, b) { return new Date(a.start) - new Date(b.start); });
        d.seats = d.slots.reduce(function (sum, s) { return sum + slotSeats(s); }, 0);
        return d;
      }).sort(function (a, b) {
        return new Date(a.slots[0].start) - new Date(b.slots[0].start) || a.city.name.localeCompare(b.city.name);
      });
      self.render();
      self.announce();
      track('lp_dates_loaded', { lp_days: self.days.length, lp_open_days: self.days.filter(function (d) { return d.seats > 0; }).length });
    }).catch(function (e) {
      if (window.console) console.error('[Pop-up landing] frames failed', e);
      self.fail();
    });
  };

  Landing.prototype.fail = function () {
    this.list.hidden = true;
    if (this.fallback) this.fallback.hidden = false;
  };

  Landing.prototype.relDay = function (d) {
    var n = daysBetween(ymd(new Date().toISOString(), d.city.tz), d.date), t = this.text;
    if (n <= 0) return t.today;
    if (n === 1) return t.tomorrow;
    return fill(t.inDays, 'n', n);
  };

  Landing.prototype.render = function () {
    var self = this, t = this.text;
    var withDates = {};
    this.days.forEach(function (d) { withDates[d.city.id] = (withDates[d.city.id] || 0) + 1; });

    // City chips: only cities that have a date, and only if there is a choice.
    this.chips.innerHTML = '';
    var chipCities = Object.keys(this.cities).map(function (id) { return self.cities[id]; })
      .filter(function (c) { return withDates[c.id]; });
    if (chipCities.length > 1) {
      var mk = function (key, label) {
        var b = el('button', 'pld__chip', label); b.type = 'button';
        b.setAttribute('aria-pressed', self.filter === key ? 'true' : 'false');
        b.addEventListener('click', function () {
          self.filter = key; self.render();
          track('lp_city_filter', { lp_city: key === 'all' ? 'all' : self.cities[key].name });
        });
        return b;
      };
      this.chips.appendChild(mk('all', t.all));
      chipCities.forEach(function (c) { self.chips.appendChild(mk(String(c.id), c.name.replace(/, [A-Z]{2}$/, ''))); });
      this.chips.hidden = false;
    }

    // Keep an open booking panel across re-renders.
    var openPanel = this.list.querySelector('.pld__booking');
    if (openPanel) openPanel.parentNode.removeChild(openPanel);
    this.list.innerHTML = '';
    var shown = this.days.filter(function (d) { return self.filter === 'all' || String(d.city.id) === self.filter; });
    if (!shown.length) this.list.appendChild(el('li', 'pld__status', t.none));
    shown.forEach(function (d) { self.list.appendChild(self.card(d)); });
    if (openPanel) {
      var owner = this.list.querySelector('[data-pld-card="' + openPanel.getAttribute('data-for') + '"]');
      if (owner) owner.parentNode.insertBefore(openPanel, owner.nextSibling);
    }

    var soon = Object.keys(this.cities).map(function (id) { return self.cities[id]; })
      .filter(function (c) { return !withDates[c.id]; })
      .map(function (c) { return c.name.replace(/, [A-Z]{2}$/, ''); });
    this.soon.hidden = !soon.length || !this.days.length;
    this.soon.textContent = fill(t.soon, 'cities', soon.join(', '));
  };

  Landing.prototype.card = function (d) {
    var self = this, t = this.text, first = d.slots[0].start, tz = d.city.tz, open = d.seats > 0;
    var li = el('li', 'pld__card' + (open ? '' : ' is-full'));
    li.setAttribute('data-pld-card', d.city.id + '|' + d.date);

    var tile = el('div', 'pld__tile'); tile.setAttribute('aria-hidden', 'true');
    tile.appendChild(el('span', 'pld__dow', fmt(first, tz, { weekday: 'short' })));
    tile.appendChild(el('span', 'pld__n', fmt(first, tz, { day: 'numeric' })));
    tile.appendChild(el('span', 'pld__mon', fmt(first, tz, { month: 'short' })));
    li.appendChild(tile);

    var body = el('div', 'pld__body');
    var top = el('div', 'pld__top');
    top.appendChild(el('h3', 'pld__city', d.city.name));
    top.appendChild(el('span', 'pld__rel', this.relDay(d)));
    body.appendChild(top);
    body.appendChild(el('p', 'pld__date', fmt(first, tz, { weekday: 'long', month: 'long', day: 'numeric' }) +
      ' · ' + d.slots.map(function (s) { return fmt(s.start, tz, { hour: 'numeric', minute: '2-digit' }); }).join(' & ')));
    if (d.city.venue) body.appendChild(el('p', 'pld__venue', d.city.venue));
    var seatsText, low = false;
    if (!open) seatsText = t.soldOut;
    else if (d.seats < (this.cfg.seatsShowBelow || 10)) { seatsText = d.seats === 1 ? t.oneSeat : fill(t.seats, 'n', d.seats); low = d.seats <= (this.cfg.lowSeats || 4); }
    else seatsText = t.available;
    body.appendChild(el('p', 'pld__seats' + (low ? ' is-low' : '') + (open ? '' : ' is-full'), seatsText));
    li.appendChild(body);

    var btn = el('button', 'plx__btn pld__book', open ? t.book : t.soldOut);
    btn.type = 'button';
    if (!open) btn.disabled = true;
    else btn.addEventListener('click', function () { self.book(d, li, btn); });
    btn.setAttribute('aria-label', (open ? t.book : t.soldOut) + ': ' + d.city.name + ', ' + fmt(first, tz, { weekday: 'long', month: 'long', day: 'numeric' }));
    li.appendChild(btn);
    return li;
  };

  // Open the city's widget under the card, on this day, in its pop-up dialog.
  Landing.prototype.book = function (d, li, btn) {
    var self = this;
    var holder = this.root.querySelector('[data-pld-holder="' + d.city.id + '"]');
    var api = window.PopupBooking;
    track('lp_book_click', { lp_city: d.city.name, booking_date: d.date });
    if (!holder || !api || typeof api.boot !== 'function') { window.location.href = d.city.url; return; }

    // One booking panel at a time, directly under the card that asked for it.
    var panel = this.list.querySelector('.pld__booking');
    if (!panel) { panel = el('li', 'pld__booking'); }
    panel.setAttribute('data-for', d.city.id + '|' + d.date);
    li.parentNode.insertBefore(panel, li.nextSibling);
    if (holder.parentNode !== panel) {
      var prev = panel.querySelector('[data-pld-holder]');
      if (prev) { prev.hidden = true; this.root.appendChild(prev); }
      panel.appendChild(holder);
    }
    holder.hidden = false;

    var wroot = holder.querySelector('[data-popup-booking], [data-popup-booking-lazy]');
    var w = wroot && (wroot.__popupBooking || api.boot(wroot));
    if (!w || !w.ready) { window.location.href = d.city.url; return; }
    var label = btn.textContent;
    btn.textContent = this.text.opening; btn.disabled = true;
    w.ready.then(function () {
      btn.textContent = label; btn.disabled = false;
      w.selectDate(d.date);
      if (w.cfg && w.cfg.popup && typeof w.openModal === 'function') w.openModal();
      else holder.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // The hero's "next pop-up" line.
  Landing.prototype.announce = function () {
    var next = this.days.filter(function (d) { return d.seats > 0; })[0];
    var line = next ? fill(fill(this.text.next, 'date', fmt(next.slots[0].start, next.city.tz, { weekday: 'long', month: 'long', day: 'numeric' })), 'city', next.city.name.replace(/, [A-Z]{2}$/, '')) : '';
    Array.prototype.forEach.call(document.querySelectorAll('[data-plx-next]'), function (n) {
      n.textContent = line; n.hidden = !line;
    });
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pld]'), function (root) {
      if (root.__pld) return;
      try { root.__pld = new Landing(root); } catch (e) { if (window.console) console.error('[Pop-up landing] init failed', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
